// Modules
import { useApolloClient } from '@apollo/client'
import React, { createContext, useCallback, useContext, useEffect, useState } from 'react'

// Application
import { ConfirmToken, CreateLogin } from 'services/signup/mutation'
import { CheckPatientFederalRevenue, SearchLoginByCPF, ValidateLoginContact, VerifyPreUserRegistration } from 'services/signup/query'

import {
    CheckPatientFederalRevenueResponse,
    ConfirmTokenData,
    Contact,
    ContactData,
    ContactLoginStatus,
    ContactTypes,
    EmailContact,
    Password,
    PatientInfoInput,
    PatientLogin,
    PersonalData,
    PhoneContact,
    PostalCode,
    SignUpState,
    UserName,
    ValidateLoginContactResponse,
} from 'typings/signup'

import { useAccountService } from 'services/account'
import { ContactType } from 'typings'
import { CreatePatientInput, CreatePatientType, FormDataVerification, PatientMDMInfoSearch, ResendTokenType } from 'typings/account'
import { RefreshToken } from 'services/gql/account/mutation'
import { RefreshTokenResponse, UserToken } from 'typings/auth'
import { useLogInfoStore } from 'redesign/store'

interface ContextData {
    hasMounted: boolean
    hasSeenFirstStep: boolean
    userId?: number
    personalData?: PersonalData
    passwords?: Password
    postalCode?: PostalCode
    contact?: Contact
    phoneContacts?: Array<PhoneContact> | undefined
    emailContacts?: Array<EmailContact> | undefined
    setSeenFirstStep(seen: boolean): void
    setPersonalData(data: PersonalData): Promise<void>
    setPassword(passwords: Password): Promise<void>
    setPostalCode(postalCodeNumber: PostalCode): Promise<void>
    verifyPreUserRegistration(patient: PatientInfoInput): Promise<FormDataVerification>
    confirmToken(token: string): Promise<boolean>
    createPatientAccount(patient: PatientInfoInput): Promise<CreatePatientType>
    setContactEmail(email: ContactData): Promise<void>
    setContactPhone(cellphone: ContactData): Promise<void>
    isLoading: boolean
    setIsLoading(loading: boolean): void
    viewerSearchLoginByCPF(cpf: UserName): Promise<void>
    createLogin(patient: PatientLogin): Promise<void>
    verifyLoginRegistration(cpf: UserName): Promise<boolean>
    validateLoginContact(contactType: ContactTypes, contactValue: string): Promise<ContactLoginStatus>
    resendToken(patient: ResendTokenType): Promise<void>
    isSubmitting: boolean
    setSubmitting(loading: boolean): void
    checkPatientFederalRevenueByCpf(cpf: string): Promise<CheckPatientFederalRevenueResponse | null>
    refreshToken(userToken: UserToken): Promise<RefreshTokenResponse>
}

const LIVIA_LS_KEY = '@Livia'

const SignUpContext = createContext<ContextData>({} as ContextData)

export const SignUpProvider: React.FC = ({ children }) => {
    const [hasMounted, setMounted] = useState<boolean>(false)
    const [loading, setLoading] = useState<boolean>(false)
    const [patientData, setPatientData] = useState<SignUpState | null>(null)
    const [hasSeenFirstStep, _setSeenFirstStep] = useState<boolean>(false)
    const [isSubmitting, setSubmitting] = useState<boolean>(false)
    const { createPatientAccount: serviceCreatePatientAccount, resendToken } = useAccountService()
    const { logInfo } = useLogInfoStore()

    useEffect(() => {
        const personalData = sessionStorage.getItem('@LiviaAuth:SignUpPersonalData')
        const biologicalSex = sessionStorage.getItem('@LiviaAuth:SignUpBiologicalSex')
        const genderIdentity = sessionStorage.getItem('@LiviaAuth:SignUpGenderIdentity')
        const postalCodeNumber = sessionStorage.getItem('@LiviaAuth:SignUpPostalCode')
        const contact = sessionStorage.getItem('@LiviaAuth:SignUpContact')
        const userId = sessionStorage.getItem('@LiviaAuth:SignUpUserId')
        const phoneContactList = sessionStorage.getItem('@LiviaAuth:SignUpPhoneList')
        const emailContactList = sessionStorage.getItem('@LiviaAuth:SignUpEmailList')
        setPatientData({
            postalCodeNumber: postalCodeNumber ? { ...JSON.parse(postalCodeNumber) } : {},
            biologicalSex: biologicalSex ? { ...JSON.parse(biologicalSex) } : {},
            genderIdentity: genderIdentity ? { ...JSON.parse(genderIdentity) } : {},
            personalData: personalData ? { ...JSON.parse(personalData) } : {},
            passwords: { password: '', confirmPassword: '' },
            contact: contact ? { ...JSON.parse(contact) } : {},
            id: userId ? { ...JSON.parse(userId) } : {},
            phoneContacts: phoneContactList ? JSON.parse(phoneContactList) : undefined,
            emailContacts: emailContactList ? JSON.parse(emailContactList) : undefined,
        })
        setMounted(true)
    }, [setPatientData])

    const setPersonalData = useCallback(
        async ({
            name,
            cpf,
            birthDate,
            term,
            accountExists,
            socialName,
            genderIdentity,
            biologicalSex,
            anonymousPatient,
        }: PersonalData) => {
            const personalData: PersonalData = {
                name,
                socialName,
                cpf,
                birthDate,
                genderIdentity,
                biologicalSex,
                term,
                accountExists,
                anonymousPatient,
            }
            sessionStorage.setItem('@LiviaAuth:SignUpPersonalData', JSON.stringify(personalData))
            setPatientData({ ...patientData, personalData })
        },
        [patientData],
    )

    const setPassword = useCallback(
        async ({ password, confirmPassword }: Password) => {
            const passwords: Password = {
                password,
                confirmPassword,
            }
            setPatientData({ ...patientData, passwords })
        },
        [patientData],
    )

    const setPostalCode = useCallback(
        async ({ postalCode }: PostalCode) => {
            const postalCodeNumber: PostalCode = {
                postalCode,
            }
            setPatientData({ ...patientData, postalCodeNumber })
            sessionStorage.setItem('@LiviaAuth:SignUpPostalCode', JSON.stringify(postalCodeNumber))
        },
        [patientData],
    )

    const setContactEmail = useCallback(
        async (email: ContactData) => {
            const contact = { ...patientData?.contact, email: email, contactType: ContactType.EMAIL }
            setPatientData({ ...patientData, contact })
            sessionStorage.setItem('@LiviaAuth:SignUpContact', JSON.stringify(contact))
        },
        [patientData],
    )

    const setContactPhone = useCallback(
        async (cellphone: ContactData) => {
            const contact = { ...patientData?.contact, cellphone: cellphone, contactType: ContactType.MOBILE }
            setPatientData({ ...patientData, contact })
            sessionStorage.setItem('@LiviaAuth:SignUpContact', JSON.stringify(contact))
        },
        [patientData],
    )

    const client = useApolloClient()

    const verifyPreUserRegistration = async (patient: PatientInfoInput): Promise<FormDataVerification> => {
        const { data } = await client.query<PatientMDMInfoSearch>({
            query: VerifyPreUserRegistration,
            fetchPolicy: 'no-cache',
            variables: {
                patient: {
                    name: patient.name,
                    cpf: patient.cpf,
                    birthDate: patient.birthDate,
                },
            },
        })

        const { viewerSearch } = data
        const { id, cep = '', biologicalSex = '', phoneContacts = [], emailContacts = [] } = viewerSearch
        const personalData: PersonalData = {
            name: patient.name,
            socialName: patient.socialName,
            cpf: patient.cpf,
            birthDate: patient.birthDate,
            term: patient.term,
        }

        const formDataVerification: FormDataVerification = {
            id: id,
            postalCode: cep,
            biologicalSex,
            phoneContacts,
            emailContacts,
        }

        sessionStorage.setItem('@LiviaAuth:SignUpPhoneList', JSON.stringify(phoneContacts))
        sessionStorage.setItem('@LiviaAuth:SignUpEmailList', JSON.stringify(emailContacts))
        sessionStorage.setItem('@LiviaAuth:SignUpUserId', JSON.stringify(id))
        sessionStorage.setItem('@LiviaAuth:SignUpPersonalData', JSON.stringify(personalData))
        sessionStorage.setItem('@LiviaAuth:SignUpPostalCode', JSON.stringify(cep))
        sessionStorage.setItem('@LiviaAuth:SignUpBiologicalSex', JSON.stringify(biologicalSex))

        return formDataVerification
    }

    const confirmToken: (token: string) => Promise<boolean> = async (token: string) => {
        const userName = patientData?.personalData?.cpf
        const password = patientData?.passwords?.password
        const dataConfirmation = {
            userName,
            token,
            password,
        }

        const { data } = await client.mutate<ConfirmTokenData>({
            mutation: ConfirmToken,
            variables: {
                dataConfirmation,
                logInfo,
            },
        })

        if (!data || !data.confirmToken || !data.confirmToken.authentication) {
            return false
        }

        const { confirmToken: payloadData } = data

        if (payloadData.authentication) {
            localStorage.setItem(
                LIVIA_LS_KEY,
                JSON.stringify({
                    token: payloadData.authentication.access_token,
                    refreshToken: payloadData.authentication.refresh_token,
                }),
            )

            return true
        }

        return false
    }

    const createPatientAccount = async (patient: CreatePatientInput) => {
        const data = await serviceCreatePatientAccount(patient)

        return data
    }

    const setIsLoading = (isLoading: boolean) => {
        setLoading(isLoading)
    }

    const viewerSearchLoginByCPF = async ({ username }: UserName): Promise<void> => {
        await client.query<UserName>({
            query: SearchLoginByCPF,
            fetchPolicy: 'no-cache',
            variables: {
                username,
            },
        })
    }

    const verifyLoginRegistration = async ({ username }: UserName): Promise<boolean> => {
        try {
            await viewerSearchLoginByCPF({ username })
            return true
        } catch (error) {
            return false
        }
    }

    const validateLoginContact = async (contactType: ContactTypes, contactValue: string): Promise<ContactLoginStatus> => {
        const { data } = await client.query<ValidateLoginContactResponse>({
            query: ValidateLoginContact,
            fetchPolicy: 'no-cache',
            variables: {
                contactType,
                contactValue,
            },
        })

        const {
            validateLoginContact: { status },
        } = data as ValidateLoginContactResponse

        return status
    }

    const createLogin = async (patient: PatientLogin): Promise<void> => {
        await client.mutate<PatientLogin>({
            mutation: CreateLogin,
            variables: {
                patient,
            },
        })
    }

    const checkPatientFederalRevenueByCpf = async (federalTaxId: string): Promise<CheckPatientFederalRevenueResponse> => {
        const {
            data: { checkPatientFederalRevenue },
        } = await client.query<CheckPatientFederalRevenueResponse>({
            query: CheckPatientFederalRevenue,
            fetchPolicy: 'no-cache',
            variables: {
                federalTaxId,
            },
        })
        return { checkPatientFederalRevenue }
    }

    const setSeenFirstStep = (seen: boolean) => {
        _setSeenFirstStep(seen)
    }

    const refreshToken = async (userToken: UserToken): Promise<RefreshTokenResponse> => {
        delete userToken?.keepLogged?.keepLogged
        delete userToken?.keepLogged?.username
        if (userToken.token) {
            const { data } = await client.mutate<RefreshTokenResponse>({
                mutation: RefreshToken,
                variables: {
                    token: userToken.refreshToken,
                    KeepLogged: userToken?.keepLogged,
                },
            })
            return {
                refreshToken: {
                    access_token: data?.refreshToken.access_token,
                    refresh_token: data?.refreshToken.refresh_token,
                },
            }
        }

        return {
            refreshToken: {
                access_token: null,
                refresh_token: null,
            },
        }
    }

    return (
        <SignUpContext.Provider
            value={{
                hasMounted,
                hasSeenFirstStep,
                personalData: patientData?.personalData,
                passwords: patientData?.passwords,
                postalCode: patientData?.postalCodeNumber,
                contact: patientData?.contact,
                userId: patientData?.id,
                phoneContacts: patientData?.phoneContacts,
                emailContacts: patientData?.emailContacts,
                setSeenFirstStep,
                setPostalCode,
                setPersonalData,
                verifyPreUserRegistration,
                confirmToken,
                createPatientAccount,
                setContactEmail,
                setContactPhone,
                setPassword,
                isLoading: loading,
                setIsLoading,
                viewerSearchLoginByCPF,
                createLogin,
                verifyLoginRegistration,
                validateLoginContact,
                resendToken,
                setSubmitting,
                isSubmitting,
                checkPatientFederalRevenueByCpf,
                refreshToken,
            }}
        >
            {children}
        </SignUpContext.Provider>
    )
}

export function useSignUp(): ContextData {
    const context = useContext(SignUpContext)
    if (context === undefined) {
        throw new Error('useSignUp value is undefined. Make sure you use the AuthProvider before using the context.')
    }
    return context
}
